import React, { FC } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from '$hooks';
import { IStoreInformation, StockType } from '$models';
import { Flex } from '$components/layouts';
import { formatStringWithContent } from '$lib/helpers';
import { Text } from '$components/elements/text';
import { Button } from '$components/elements/button';
import { B2CDeliveryMethod } from '$constants';
import { SvgIcon } from '$components/elements/svg-icon';

type Props = {
    deliveryMethod?: B2CDeliveryMethod;
    selectedStore?: IStoreInformation;
    selectedStoreStockType?: StockType;
    stockCount?: number;
    setPanelVisible: (isVisible: boolean, hasStore?: boolean) => void;
};

export const StockSummary: FC<Props> = ({
    deliveryMethod,
    selectedStore,
    selectedStoreStockType,
    stockCount = -1,
    setPanelVisible,
}): JSX.Element => {
    const { translate } = useTranslation();

    const theme = useTheme();

    if (
        selectedStore &&
        deliveryMethod === B2CDeliveryMethod.DirectOnly &&
        (selectedStoreStockType === 'noStock' || selectedStoreStockType === 'unknown')
    ) {
        return <></>;
    }

    return (
        <ProductStockWrapper gap="extrasmall" alignItems="center">
            <InformationIconWrapper color={stockCount <= 0 ? theme.colors.unknownStock : theme.colors.inStock}>
                {stockCount > 0 && <SvgIcon size={10} svg="checkmark" color={theme.colors.white} />}
                {/* <SvgIcon size={10} svg={stockCount <= 0 ? 'store' : 'checkmark'} color={theme.colors.white} /> */}
            </InformationIconWrapper>
            <StatusText variant="body">
                {formatStringWithContent(
                    translate('productPage.productStock.inStockNoStoreMessage'),
                    <strong>{stockCount}</strong>
                )}
            </StatusText>
            <StoreSelectButton variant="link" color={theme.colors.red} onClick={() => setPanelVisible(true)}>
                {selectedStore
                    ? translate('productPage.productStock.selectedStoreChangeStoreButton')
                    : translate('productPage.productStock.modalChooseStoreButton')}
            </StoreSelectButton>
        </ProductStockWrapper>
    );
};

const ProductStockWrapper = styled(Flex)(() => ({
    marginBottom: 0,
}));

const StatusText = styled(Text)(({ theme }) => ({
    marginRight: theme.space[1],
}));

const StoreSelectButton = styled(Button)(({ theme }) => ({
    color: theme.colors.red,
}));

const InformationIconWrapper = styled.div<{ color: string }>(({ color, theme }) => ({
    marginRight: theme.space[1],
    borderRadius: 50,
    backgroundColor: color,
    width: '20px',
    height: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
}));
